<!-- eslint-disable vue/no-v-html -->
<template>
  <tr v-for="(event, eventIndex) in renderedEvents" :key="eventIndex">
    <!-- SESSION ROW -->
    <td v-if="event.type === 'session'" colspan="3">
      <b v-html="normalizedTitle(event)" /><br />

      <span v-if="event.subtitle">
        <i>{{ event.subtitle }}: </i>

        <PersonLink v-if="shouldLinkSubtitle(event)" :url="event.url">
          <span v-if="event.name"
            >{{ normalizedName(event)
            }}<small v-if="event.status" class="status">{{ event.status }}</small></span
          >
          <span v-else-if="event.venue">{{ event.venue }}</span>
        </PersonLink>

        <span v-else-if="isAnyUndecided(event)">
          {{ normalizeUndecided() }}
        </span>

        <span v-else-if="event.name">
          ({{ normalizedName(event)
          }}<small v-if="event.status" class="status">{{ event.status }}</small>)
        </span>

        <span v-else-if="event.venue">
          {{ event.venue }}
        </span>
      </span>
    </td>

    <!-- TIME -->
    <td v-if="event.type !== 'session'" class="time">
      {{ formatTime(event.start)
      }}<span v-if="event.end" class="wideScreenOnly">&#x202F;&ndash;&#x2009;{{
        formatTime(event.end)
      }}</span>
    </td>

    <!-- VIDEO -->
    <td v-if="event.type !== 'session'">
      <VideoIcon
        :event="event"
        :join-link="getJoinLink(event.sessionTitle)" />
    </td>

    <!-- EVENT DETAILS -->
    <td v-if="event.type !== 'session'">
      <span class="event">
        <span v-if="!description" class="arrows">
          <button
            v-if="hasToggle(event)"
            type="button"
            class="chevronButton"
            :aria-expanded="isExpanded(event.ref) ? 'true' : 'false'"
            :aria-label="plainTitle(event)"
            @click="toggleDescription(event.ref)">
            <span :class="{hide: isExpanded(event.ref)}" class="right chevronArrow" aria-hidden="true" />
            <span :class="{hide: !isExpanded(event.ref)}" class="down chevronArrow" aria-hidden="true" />
          </button>
        </span>

        <span v-if="event.title" class="eventTitle">
          <!-- the title also expands/collapses the details (a mouse shortcut; the chevron is the
               keyboard control); an expanded talk's title is in the year's brand colour -->
          <span
            class="toggleTitle"
            :class="{isToggle: canToggleFromTitle(event), isOpenTitle: isOpen(event)}"
            @click="onTitleClick(event, $event)"
          ><span v-if="event.type === 'keynote'"><b>Keynote: </b></span
          ><!-- IMPORTANT: title may contain HTML --><span v-html="normalizedTitle(event)"
          /></span>

          <!-- SPEAKER -->
          <template v-if="showParenUndecided(event)"> ({{ normalizeUndecided() }}) </template>

          <template v-else-if="hasJointSpeakers(event)">
            (<template v-for="(speaker, index) in event.speakers" :key="index"
            ><span v-if="index">{{ index === event.speakers.length - 1 ? " & " : ", " }}</span
            ><PersonLink v-if="speakerUrl(speaker.name)" :url="speakerUrl(speaker.name)">{{
              speaker.name
            }}</PersonLink
            ><span v-else>{{ speaker.name }}</span
            ><small v-if="speaker.status" class="status">{{ speaker.status }}</small></template
            >)
          </template>

          <template v-else-if="hasSingleSpeaker(event)">
            (<PersonLink :url="speakerUrl(normalizedName(event))">{{
              normalizedName(event)
            }}</PersonLink
            ><small v-if="event.status" class="status">{{ event.status }}</small>)
          </template>

          <template v-else-if="event.name">
            ({{ normalizedName(event)
            }}<small v-if="event.status" class="status">{{ event.status }}</small>)
          </template>

          <Slides v-if="hasSingleSpeaker(event)" :name="normalizedName(event)" align="left" />

          <!-- the videos to watch for this item: its own, or the earlier full-length talk it
               repeats or summarises (e.g. November's online talks: April's; not for breakouts) -->
          <span v-if="hasVideoLinks(event)" class="videoLinks">
            <router-link
              v-for="video in event.videos"
              :key="video.hash"
              :to="`/Videos/${video.hash}`"
              class="videoLink">
              <VideoPlayIcon :label="getVideoLinkLabel(event, video)" />
            </router-link>
          </span>

          <!-- a summary talk or breakout built on earlier recordings: watch them first -->
          <ViewingNote
            v-if="hasToggle(event) && event.viewingNote"
            :note="event.viewingNote"
            :learn-more-path="{path: `/${meetingYear}`, hash: '#Faraday'}"
            :class="{hide: !description && !isExpanded(event.ref)}" />

          <!-- IMPORTANT: description may contain HTML -->
          <p
            v-if="hasToggle(event)"
            :class="{hide: !description && !isExpanded(event.ref), description: true}"
            v-html="event.description" />
        </span>
      </span>
    </td>
  </tr>
</template>

<script setup>
const props = defineProps([
  "programDay",
  "session",
  "description",
  "timeZoneSelected",
  "videoOwners" // Map videoHash → ref of the program event it records (Table.vue)
]);

import Slides from "@/components/People/Person/Slides";
import PersonLink from "@/components/Program/PersonLink";
import VideoIcon from "@/components/Program/VideoIcon";
import VideoPlayIcon from "@/components/Program/VideoPlayIcon";
import ViewingNote from "@/components/Program/ViewingNote";
import useEventVideos from "@/composables/useEventVideos";
import useJoinLink from "@/composables/useJoinLink";
import {reactive, computed} from "vue";

const {getJoinLink} = useJoinLink();
const {getEventVideos, getViewingNote} = useEventVideos();

/* ---------- description toggle state ---------- */

const expandedDescriptions = reactive({});

function isExpanded(refKey) {
  return !!expandedDescriptions[refKey];
}

function toggleDescription(refKey) {
  expandedDescriptions[refKey] = !expandedDescriptions[refKey];
}

function hasToggle(event) {
  return (!!event.description || !!event.viewingNote) && !isTitleLinked(event);
}

// expanded by the user (not when every description is shown)
function isOpen(event) {
  return hasToggle(event) && isExpanded(event.ref);
}

function canToggleFromTitle(event) {
  return hasToggle(event) && !props.description;
}

// a link inside the title still just follows the link
function onTitleClick(event, clickEvent) {
  if (!canToggleFromTitle(event)) return;
  if (clickEvent.target.closest("a")) return;
  toggleDescription(event.ref);
}

function plainTitle(event) {
  const html = String(normalizedTitle(event) || "");
  return html.replace(/<[^>]*>/g, "").trim();
}

/* ---------- normalization ---------- */

function normalizeRaw(value) {
  return String(value || "")
    .replace(/\./g, "")
    .replace(/\s+/g, "")
    .toUpperCase();
}

function normalizeUndecided() {
  return "T.B.A.";
}

function isUndecidedValue(value) {
  return normalizeRaw(value) === "TBA";
}

function normalizedName(event) {
  if (isUndecidedValue(event?.name)) return normalizeUndecided();
  return event?.name;
}

function normalizedTitle(event) {
  if (isUndecidedValue(event?.title)) return normalizeUndecided();
  return event?.title;
}

/* ---------- undecided state ---------- */

function isNameUndecided(event) {
  return isUndecidedValue(event?.name);
}

function isTitleUndecided(event) {
  return isUndecidedValue(event?.title);
}

function isAnyUndecided(event) {
  return isNameUndecided(event) || isTitleUndecided(event);
}

/* ---------- meeting year + speaker URL ---------- */

const meetingYear = computed(() => {
  const text = String(props?.programDay?.date || "");
  const match = text.match(/(\d{4})\s*$/);
  return match ? match[1] : "";
});

function slugifySpeakerName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, "_");
}

function speakerUrl(name) {
  const year = String(meetingYear.value || "");
  const slug = slugifySpeakerName(name);
  if (!year || !slug) return "";
  return `http://vizbi.org/${year}/People#${slug}`;
}

/* ---------- joint speakers (merge consecutive rows) ---------- */

function getTitleParts(title) {
  const text = String(title || "").trim();
  if (!text) return {baseTitle: "", speaker: ""};
  const match = text.match(/^(.*)\s+\(([^()]+)\)\s*$/);
  if (!match) return {baseTitle: text, speaker: ""};
  return {baseTitle: match[1].trim(), speaker: match[2].trim()};
}

function getMillis(date) {
  if (!date?.toMillis) return null;
  return date.toMillis();
}

function isSameTimeRange(a, b) {
  const aStart = getMillis(a?.start);
  const bStart = getMillis(b?.start);
  const aEnd = getMillis(a?.end);
  const bEnd = getMillis(b?.end);
  if (aStart === null || bStart === null) return false;
  if (aStart !== bStart) return false;
  if (aEnd === null || bEnd === null) return false;
  return aEnd === bEnd;
}

function areMergeCandidates(a, b) {
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if (!isSameTimeRange(a, b)) return false;
  const aBase = getTitleParts(a.title).baseTitle;
  const bBase = getTitleParts(b.title).baseTitle;
  if (!aBase || !bBase) return false;
  if (aBase !== bBase) return false;
  const aName = String(a.name || "").trim();
  const bName = String(b.name || "").trim();
  if (!aName || !bName) return false;
  if (aName === bName) return false;
  return true;
}

function getSpeakerList(event) {
  if (Array.isArray(event?.speakers) && event.speakers.length) return event.speakers;
  const name = String(event?.name || "").trim();
  return name ? [{name, status: event?.status || ""}] : [];
}

function joinSpeakerNames(names) {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

function mergeTwoEvents(a, b) {
  const merged = {...a};
  merged.speakers = [...getSpeakerList(a), ...getSpeakerList(b)];
  merged.name = joinSpeakerNames(merged.speakers.map((speaker) => speaker.name));
  return merged;
}

function mergeConsecutiveEvents(events) {
  if (!Array.isArray(events) || events.length < 2) return events || [];
  const merged = [];
  for (let i = 0; i < events.length; i++) {
    let current = events[i];
    while (areMergeCandidates(current, events[i + 1])) {
      current = mergeTwoEvents(current, events[i + 1]);
      i += 1;
    }
    merged.push(current);
  }
  return merged;
}

function annotateWithSession(events) {
  let currentSessionTitle = null;
  return events.map((event) => {
    if (event.type === "session") currentSessionTitle = event.title;
    return {...event, sessionTitle: currentSessionTitle};
  });
}

function getSpeakerNames(event) {
  return getSpeakerList(event).map((speaker) => speaker.name);
}

function annotateWithShowcase(events) {
  const year = meetingYear.value;
  const videoOwners = props.videoOwners;
  return events.map((event) => {
    const speakerNames = getSpeakerNames(event);
    const videos = getEventVideos(event, {speakerNames, year, videoOwners});
    return {...event, ...videos, viewingNote: getViewingNote(event, videos)};
  });
}

function hasVideoLinks(event) {
  if (!event.videos.length) return false;
  return !!event.ownVideoHash || event.viewingNote?.kind !== "breakout session";
}

function getVideoLinkLabel(event, video) {
  if (event.ownVideoHash) return "View the video.";
  const names = video.speakers.join(" & ");
  return names ? `View ${names}’s full recorded talk.` : "View the video.";
}

const renderedEvents = computed(() =>
  annotateWithShowcase(annotateWithSession(mergeConsecutiveEvents(props?.programDay?.events || [])))
);

function hasJointSpeakers(event) {
  return Array.isArray(event?.speakers) && event.speakers.length >= 2;
}

function hasSingleSpeaker(event) {
  const name = String(normalizedName(event) || "").trim();
  if (!name) return false;
  if (isUndecidedValue(name)) return false;
  if (hasJointSpeakers(event)) return false;
  return !!speakerUrl(name);
}

/* ---------- rendering rules ---------- */

function isTitleLinked(event) {
  const title = String(event?.title || "").trim();
  return title.startsWith("<a ") && title.endsWith("</a>");
}

function showParenUndecided(event) {
  return isNameUndecided(event) && !isTitleUndecided(event);
}

function shouldLinkSubtitle(event) {
  return !!event.subtitle && !!event.url && !isAnyUndecided(event);
}

/* ---------- time ---------- */

// zones whose people mostly read 12-hour clocks ("5:00pm"); elsewhere 24-hour (Seán, 2026-10-27:
// by the zone chosen, not the browser's language, which is often "English (US)" anywhere)
const TWELVE_HOUR_PREFIXES = [
  "Australia/", "US/", "Canada/", "America/Indiana/", "America/Kentucky/"
];
const TWELVE_HOUR_ZONES = new Set([
  "Europe/London", "Pacific/Auckland", "Pacific/Honolulu", "Asia/Kolkata", "Asia/Calcutta",
  "Asia/Manila", "Asia/Karachi", "Africa/Cairo", "Asia/Riyadh", "America/New_York",
  "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles",
  "America/Anchorage", "America/Detroit", "America/Boise", "America/Toronto",
  "America/Vancouver", "America/Edmonton", "America/Winnipeg", "America/Regina",
  "America/Halifax", "America/St_Johns"
]);

function getIsTwelveHour(zone) {
  return TWELVE_HOUR_ZONES.has(zone) || TWELVE_HOUR_PREFIXES.some((each) => zone.startsWith(each));
}

function formatTime(date) {
  if (!date) return;
  if (!props.timeZoneSelected) return;
  if (getIsTwelveHour(props.timeZoneSelected)) return date.toFormat("h:mma").toLowerCase();
  return date.toFormat("T").toLowerCase();
}
</script>

<style scoped>
/* a note in a title (the sheet's <small>, e.g. "(speakers announced shortly)"): muted, but
   readable (the global small's colour was too faint on the striped rows) */
:deep(small) {
  color: var(--text-color-less-subtle);
}
/* a speaker not yet confirmed (the sheet's Status, TBC or TBD; Seán, 2026-11-05): after the name,
   as the sheet's notes, small; no brackets of its own inside the speaker's */
.status {
  margin-left: 0.3em; /* ▶ EDIT HERE: space between the name and TBC */
}
#programTable {
  clear: both; /* https://stackoverflow.com/q/2580772 */
  margin-top: 3.8em;
  margin-bottom: 2em;
}
tr:nth-child(even) {
  /*  https://stackoverflow.com/q/32867605 */
  background-color: var(--background-color-subtle);
}
.eventTable {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
}
.eventTable td,
th {
  padding: 6px;
  vertical-align: top;
}
/* https://stackoverflow.com/q/27788092 */
.eventTable tr td:first-child {
  width: 19%;
}
/* a time range on one line: the table widens the column, the title wraps instead (Seán,
   2026-10-29) */
.eventTable td.time {
  white-space: nowrap;
}
/* on a phone, the time column as wide as its times, and the camera evenly between the time and the
   title, 8px each side (Seán, 2026-10-29): more room for the titles */
.narrow .eventTable td.time {
  width: 1%;
  padding-right: 2px;
}
.narrow .eventTable tr td:nth-child(2) {
  padding-right: 2px;
}
.eventTable tr td:nth-child(2) {
  width: 2%;
}
.eventTable tr td:nth-child(3) {
  width: 79%;
}
h4 {
  display: inline;
  font-weight: 600 !important;
}
h5 {
  font-weight: 600 !important;
  /* this don't work - don't know why */
  margin-top: 22px;
  padding-top: 0px;
  margin-bottom: 6px;
  padding-bottom: 0px;
}
.h5_subheading {
  font-weight: 200 !important;
}
.event {
  display: flex; /* outer container for event arrow / title / description */
}
.eventTitle {
  flex-direction: column;
}
/* a speaker's name links to their page: underlined, so it's told from the title by more than its
   colour (WCAG link-in-text-block; DESIGN.md § B.1: links are underlined) */
.eventTitle :deep(a) {
  text-decoration: underline;
  text-underline-offset: 0.15em;
}
span.arrows {
  display: flex;
  flex-direction: column;
  color: #666666;
  padding-left: 0px;
  margin-left: 0px;
  cursor: pointer;
}
/* keyboard-accessible chevron toggle; visually identical to the old span */
button.chevronButton {
  display: flex;
  flex-direction: column;
  background: none;
  border: none;
  border-radius: 0; /* undo global.css's button look (shadow, rounded corners) */
  box-shadow: none;
  padding: 0;
  margin: 0;
  color: inherit;
  cursor: pointer;
}
/* https://stackoverflow.com/q/27492191 */
.chevronArrow {
  border-right: 0.1em solid #7f7f7f;
  border-top: 0.1em solid #7f7f7f;
  width: 0.4em;
  height: 0.4em;
  margin-top: 0.4em;
  margin-left: 0em;
  margin-right: 0.6em;
  margin-bottom: 0.1em;
}
.hide {
  display: none;
}
.isOpenTitle {
  color: var(--brand-color); /* per year/theme (global.css) */
}
/* the video link(s) at the end of an item, after the speakers */
.videoLinks {
  margin-left: 0.4em;
  white-space: nowrap;
}
.videoLink + .videoLink {
  margin-left: 0.3em;
}
.right {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}
.down {
  transform: rotate(135deg);
  -webkit-transform: rotate(135deg);
}
.down {
  border-color: var(--brand-color); /* open */
}
/* hover (the title's too): stronger, not the brand colour, which says open (DESIGN.md § 15) */
span.chevronArrow:not(.down):hover,
.event:has(.toggleTitle.isToggle:hover) .chevronArrow:not(.down) {
  border-color: var(--text-color);
}
.toggleTitle.isToggle {
  cursor: pointer;
}
/* key adjustments for small screen */
.narrow h4,
.narrow h4 {
  font-size: 1.25rem;
}
.narrow .eventTable col.first,
.medium .eventTable col.first {
  width: 8%;
}
.narrow .eventTable col.second,
.medium .eventTable col.second {
  width: 3%;
}
.narrow .eventTable col.third,
.medium .eventTable col.third {
  width: 89%;
}
.narrow .wideScreenOnly,
.medium .wideScreenOnly {
  /* show only start times on narrow screens */
  display: none;
}ul.legend {
  margin-left: 0px;
  padding-left: 0px;
  margin-top: 12px;
  margin-bottom: 0px;
  padding-bottom: 0px;
  font-weight: 300;
}
ul.legend li {
  display: inline;
}
li + li {
  margin-left: 6px;
}
li:not(:first-of-type) + li::before {
  margin-right: 6px;
  content: " | "; /* https://stackoverflow.com/q/9171699 */
}
</style>
